import React, { useState } from 'react';
import uuid from 'uuid';
import styled from 'styled-components';
import { Tooltip } from '@material-ui/core';
import InfoIcon from 'components/icons/info';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import {
  ButtonContainer,
  CloseIcon,
  SaveFormulaButton,
  CancelButton,
  TitleContainer,
  Title,
  ContentSection,
  Container,
  Content,
  ContentWithScroll,
} from 'components/formula-entry/modals/_common';
import { EDealField } from 'store/deals/types';
import colors from 'theme/colors';
import { NON_STANDARD_RISK_DESCRIPTION, TITLE, CHOOSE_ALL_TEXT, TERMS } from './types';

const { fontBody } = colors;

const RadioButton = withStyles({
  root: {
    '&.Mui-checked': {
      color: `${fontBody}`,
    },
  },
})(Radio);

const useStyles = makeStyles({
  noMaxWidth: {
    maxWidth: '600px',
    color: 'white',
    borderRadius: '2px',
    background: '#444444',
  },
});

const RadioContainer = styled.div`
  width: 95%;
  display: flex;
  justify-content: space-between;
  background: #ffffff;
  border-radius: 3px;
  margin-bottom: 10px;
  padding: 8px;
  align-items: baseline;
`;

const TitleWrap = styled(TitleContainer)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardsContainer = styled.div`
  width: 100%;
`;

const LargeSection = styled.div`
  width: 86%;
`;

const TextContainer = styled(TitleWrap)`
  color: white;
  justify-content: space-between;
  font-size: 14px;
  margin: 10px;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 35%;
  justify-content: space-around;
`;

const DisplayText = styled.div`
  font-size: 16px;
`;
const NotText = styled.span`
  font-weight: 600;
  margin-right: 6px;
`;

export interface IAdditionalMandateModalInterface {
  nonStandardRisksDefault:
    | { options: { radioName: string; selected: boolean }[]; name?: string; displayName: string }[]
    | undefined;
  updateDealField: Function;
  getDealEvaluation: Function;
  setEnableModal: Function;
  enableModal: boolean;
}

export default ({
  nonStandardRisksDefault,
  setEnableModal,
  enableModal,
  updateDealField,
  getDealEvaluation,
}: IAdditionalMandateModalInterface) => {
  const [cards, setCards] = useState(nonStandardRisksDefault ?? []);
  const classes = useStyles();

  const onInputChange = ({ target }: { target: { name: string; value: string } }) => {
    const nexState = cards?.map(
      (card: { displayName: string; options: { radioName: string; selected: boolean }[] }) => {
        if (card.displayName !== target.name) {
          return card;
        }
        return {
          ...card,
          options: card.options.map(opt => {
            const checked = opt.radioName === target.value;
            return {
              ...opt,
              selected: checked,
            };
          }),
        };
      }
    );
    setCards(nexState);
  };

  const applyClicked = () => {
    const arr: string[] = [];
    cards.forEach((dt: { options: { radioName: string; selected: boolean }[]; name?: string }) => {
      return dt.options.filter(opt => {
        if (opt.radioName === 'Included' && opt.selected) {
          arr.push(dt.name ? dt.name : '');
          return arr;
        }
        return null;
      });
    });
    updateDealField(EDealField.NON_STANDARD_RISKS, arr);
    getDealEvaluation();
    setEnableModal(!enableModal);
  };

  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleWrap>
            <Title>{TITLE}</Title>
            <Tooltip
              title={NON_STANDARD_RISK_DESCRIPTION}
              classes={{ tooltip: classes.noMaxWidth }}
              data-testid="tool-tip-id"
            >
              <InfoIcon />
            </Tooltip>
            <CloseIcon onClick={() => setEnableModal(!enableModal)} />
          </TitleWrap>
          <LargeSection>
            <TextContainer>
              <div>{TERMS}</div>
              <div>{CHOOSE_ALL_TEXT}</div>
            </TextContainer>
          </LargeSection>
          <ContentWithScroll>
            <Content>
              <ContentSection>
                <CardsContainer>
                  {cards?.map((nsr: { displayName: string; options: { radioName: string; selected: boolean }[] }) => (
                    <RadioContainer key={uuid()}>
                      <DisplayText>{nsr.displayName}</DisplayText>
                      <OptionsContainer key={uuid()}>
                        {nsr.options.map(lo => {
                          return (
                            <div key={uuid()}>
                              <RadioButton
                                key={uuid()}
                                name={nsr.displayName}
                                value={lo.radioName}
                                checked={!!lo.selected}
                                onChange={onInputChange}
                              />
                              <label htmlFor="radio_btn">
                                {lo.radioName === 'Included' ? (
                                  lo.radioName
                                ) : (
                                  <>
                                    <NotText>{lo.radioName.split(' ')[0]}</NotText>
                                    <span>{lo.radioName.split(' ')[1]}</span>
                                  </>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </OptionsContainer>
                    </RadioContainer>
                  ))}
                </CardsContainer>
              </ContentSection>
            </Content>
          </ContentWithScroll>

          <ButtonContainer>
            <CancelButton data-testid="tid-cancel-button" onClick={() => setEnableModal(!enableModal)}>
              Cancel
            </CancelButton>
            <SaveFormulaButton data-testid="tid-apply-button" onClick={applyClicked}>
              Apply
            </SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
